import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { AppMessageService } from '../../app-message.service';
import { CacheKey } from '../constants/cachekey.constants';
import { DynamicPageSlugNames } from '../constants/dynamic-page-slug.constant';
import { Subscription } from 'rxjs/Subscription';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { StoreConfig } from '../interfaces/store-config.interface';
import { RoutePath } from '../enums/route-path.enum';
import { MapLocationInfo } from '../interfaces/map-location-info.interface';
import { CacheService } from '../services/cache.service';
import { CommonService } from '../services/common.service';
import { MenuItem } from '../interfaces/menu-item.interface';
import { DynamicRoute } from 'src/app/common/interfaces';

@Component({
    selector: 'app-aboutus-mega-menu',
    templateUrl: '../templates/template1/views/aboutus-mega-menu.component.html',
    styleUrls: ['../templates/template1/themes/default/less/aboutus-mega-menu.component.less'],
    animations: [
        trigger('slideInOut', [
            state('in', style({
                overflow: 'hidden',
                height: '*',
                opacity: '1'
            })),
            state('out', style({
                opacity: '0',
                overflow: 'hidden',
                height: '0px'
            })),
            transition('in <=> out', animate('400ms ease-in-out'))
        ])
    ]
})
export class AboutUsMegaMenuComponent implements OnInit, OnDestroy {
    @Input() aboutMenuCollapsed = false;
    store: StoreConfig;
    languageCode: string;
    countryCode: string;
    cardsContent: any[];
    siteMapId: number;
    enrollCountryCode: string;
    routePath = RoutePath;
    siteMapsSubscription: Subscription;
    loadSiteMaps = false;
    mapLocation: MapLocationInfo;

    constructor(
        private cacheService: CacheService,
        private commonService: CommonService,
        private appMessageService: AppMessageService,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.languageCode = this.cacheService.getCookieValue(CacheKey.languageCode);
        this.countryCode = this.cacheService.getCookieValue(CacheKey.countryCode);
        this.store = this.cacheService.get(CacheKey.StoreConfiguration);
        this.getMenus();
        this.getSiteMapsSubject();
        this.getMapLocation();
    }


    /**
     * @description This method used to get sitemap id for about header
     * getContent
     * @memberof AboutUsMegaMenuComponent
     */
    getMenus(): void {
        const menus = this.cacheService.get(CacheKey.SiteMaps);
        // About header dynamic page
        if (menus && menus.length) {
            const aboutHeader: MenuItem = menus.find(x => x.slug === DynamicPageSlugNames.ABOUT_HEADER);
            if (aboutHeader) {
                const siteMapId = aboutHeader.siteMapId;
                if (siteMapId) {
                    this.getCardsContent(siteMapId);
                }
            }
        } else {
            this.loadSiteMaps = true;
        }
    }

    /**
    * @description
    * @date 2019-11-11
    * @private
    * @memberof MegaMenuComponent
    */
    private getSiteMapsSubject(): void {
        this.siteMapsSubscription = this.appMessageService.getSiteMapsSubject().subscribe(x => {
            if (this.loadSiteMaps) {
                this.loadSiteMaps = false;
                this.getMenus();
            }
        });
    }

    /**
     * @description get cards content
     * @param {number} sitemapId
     * @memberof AboutUsMegaMenuComponent
     */
    getCardsContent(sitemapId: number) {
        this.commonService.getCardsContentBySitemapId(sitemapId).subscribe(response => {
            if (response) {
                this.cardsContent = response;
                const cards: any = document.querySelectorAll('.aboutus-block div[data-t-card]');
                if (cards) {
                    for (const card in cards) {
                        if (Number(card) >= 0) {
                            const temp = cards[card].getAttribute('data-t-card');
                            if (this.cardsContent) {
                                // tslint:disable-next-line:forin
                                for (const place in this.cardsContent) {
                                    const placeholder = this.cardsContent[place];
                                    if (placeholder.indexId === temp) {
                                        if (placeholder.cardContent !== '') {
                                            cards[card].innerHTML = placeholder.cardContent;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }, error => {

        });
    }

    /**
   * @description  navigate to portal
   * @author njagadeesan
   * @date 2020-01-11
   * @param {DynamicRoute} dynamicRoute
   * @memberof AboutUsMegaMenuComponent
   */
    navigateToPortal(dynamicRoute: DynamicRoute): void {
        let route = dynamicRoute.route;
        if (route) {
            if (environment.betaPortal === true) {
                route = this.commonService.constructBetaUrl(route);
            }
            window.open(route, dynamicRoute.target);
        }
    }

    /**
     * @description navigate to site
     * @date 2019-11-11
     * @param {*} route
     * @memberof AboutUsMegaMenuComponent
     */
    navigateToAboutSite(route: string): void {
        this.setCollapse(false);
        this.router.navigate(['/' + this.countryCode.toLowerCase()
            + '/' + this.languageCode.toLowerCase() + '/' + route]);
    }

    /**
     * @description set collapse
     * @date 2019-11-11
     * @param {boolean} isCollapse
     * @param {*} event
     * @memberof AboutUsMegaMenuComponent
     */
    setCollapse(isCollapse: boolean, event?: any): void {
        if (event) {
            event.stopPropagation();
        }
        this.appMessageService.setCollapseMenu(isCollapse);
    }

    /**
     * @description get country contact information based on map location
     * @date 2019-11-11
     * @memberof AboutUsMegaMenuComponent
     */
    getMapLocation(): void {
        this.commonService.getMapLocations().subscribe((locations: MapLocationInfo[]) => {
            if (locations) {
                this.setMapLocation(locations);
            }
        });
    }

    /**
     * @description Set map locations based upon country/home country code
     * @author njagadeesan
     * @date 2020-01-31
     * @param {MapLocationInfo[]} locations
     * @memberof AboutUsMegaMenuComponent
     */
    setMapLocation(locations: MapLocationInfo[]): void {
        let mapLocation;
        mapLocation = locations.find(item => item.countryCode.toLowerCase() === this.countryCode.toLowerCase());
        if (!mapLocation) {
            const homeCoutryCode = this.store && this.store.operatingCountry;
            mapLocation = locations.find(item => item.countryCode.toLowerCase() === homeCoutryCode.toLowerCase());
        }
        this.mapLocation = mapLocation;
    }

    // tslint:disable-next-line:use-life-cycle-interface
    ngOnDestroy(): void {
        if (this.siteMapsSubscription) {
            this.siteMapsSubscription.unsubscribe();
        }
    }
}
